'use strict';

export function initMenuHighlighting() {
  /**
   * Active menu highlighting based on scroll position
   * Adds 'is-active' class to menu items when scrolling to corresponding sections
   */

    // Configuration
  const config = {
      menuSelector: '.menu-root .menu-link',
      activeClass: 'is-active',
      offset: 100, // Offset from top in pixels (adjust based on fixed header height)
      throttleDelay: 100 // Throttle scroll event for better performance
    };

  // Get all menu links and sections
  const menuLinks = document.querySelectorAll(config.menuSelector);
  const sections = [];

  // Build sections array from menu links
  menuLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href) {
      let sectionId = null;

      // Support both #something and /#something formats (Drupal)
      if (href.startsWith('#')) {
        sectionId = href.substring(1);
      } else if (href.startsWith('/#')) {
        sectionId = href.substring(2);
      }

      if (sectionId) {
        const section = document.getElementById(sectionId);
        if (section) {
          sections.push({
            id: sectionId,
            element: section,
            link: link
          });
        }
      }
    }
  });

  /**
   * Remove active class from all menu items
   */
  function removeAllActive() {
    menuLinks.forEach(link => {
      link.classList.remove(config.activeClass);
    });
  }

  /**
   * Add active class to specific menu item
   */
  function setActive(link) {
    removeAllActive();
    link.classList.add(config.activeClass);
  }

  /**
   * Determine which section is currently in view
   */
  function getCurrentSection() {
    const scrollPosition = window.scrollY + config.offset;

    // Check from bottom to top to prioritize sections higher on page
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      const sectionTop = section.element.offsetTop;

      if (scrollPosition >= sectionTop) {
        return section;
      }
    }

    // If no section matches, return the first one
    return sections[0];
  }

  /**
   * Update active menu item based on scroll position
   */
  function updateActiveMenuItem() {
    const currentSection = getCurrentSection();

    if (currentSection) {
      setActive(currentSection.link);
    }
  }

  /**
   * Throttle function to limit how often a function can be called
   */
  function throttle(func, delay) {
    let timeoutId;
    let lastRan;

    return function() {
      const context = this;
      const args = arguments;

      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(function() {
          if ((Date.now() - lastRan) >= delay) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, delay - (Date.now() - lastRan));
      }
    };
  }

  /**
   * Smooth scroll to section when clicking menu link
   */
  function handleMenuClick(e) {
    const href = this.getAttribute('href');

    if (href) {
      let sectionId = null;

      // Support both #something and /#something formats
      if (href.startsWith('#')) {
        sectionId = href.substring(1);
      } else if (href.startsWith('/#')) {
        sectionId = href.substring(2);
      }

      if (sectionId) {
        e.preventDefault();
        const targetSection = document.getElementById(sectionId);

        if (targetSection) {
          const targetPosition = targetSection.offsetTop - (config.offset - 20);

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });

          // Update active state immediately
          setActive(this);
        }
      }
    }
  }

  /**
   * Initialize the menu scroll functionality
   */
  function init() {
    if (sections.length === 0) {
      console.warn('No sections found for menu items');
      return;
    }

    // IMPORTANT: Remove all Drupal's is-active classes first
    // Drupal adds is-active to all links pointing to <front> page
    removeAllActive();

    // Add click handlers to menu links
    menuLinks.forEach(link => {
      link.addEventListener('click', handleMenuClick);
    });

    // Add throttled scroll listener
    const throttledUpdate = throttle(updateActiveMenuItem, config.throttleDelay);
    window.addEventListener('scroll', throttledUpdate);

    // Set initial active state based on scroll position
    updateActiveMenuItem();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
